import { useState, useMemo } from 'react';
import { X } from '@/components/icons';
import { SearchInput } from './ui/search-input';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { getCountryDataList } from 'countries-list';
import { CountryFlag } from './ui/country-flag';

interface CountryCodePickerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (code: string) => void;
  selectedCode?: string;
}

export function CountryCodePickerDrawer({
  isOpen,
  onClose,
  onSelect,
  selectedCode = '+1',
}: CountryCodePickerDrawerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleSelect = (code: string) => {
    onSelect(code);
    handleClose();
  };

  const filteredCountries = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return getCountryDataList()
      .filter((country) => {
        const phoneCode = `+${country.phone[0]}`;
        return (
          country.name.toLowerCase().includes(searchLower) ||
          phoneCode.includes(searchQuery)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [searchQuery]);

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh]">
        <DrawerHeader className='flex justify-between'>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
          <DrawerTitle>Country Code</DrawerTitle>
          <div className="w-10" />
        </DrawerHeader>

        <div className="px-4 pt-3 pb-3 border-b border-gray-200">
          <SearchInput
            placeholder="Search country or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Country Code List */}
        <div className="overflow-y-auto overscroll-contain h-[400px]" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="px-4 py-2">
            {filteredCountries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-gray-500">No countries found</p>
                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
              </div>
            ) : (
              filteredCountries.map((country) => {
                const phoneCode = `+${country.phone[0]}`;
                return (
                  <button
                    key={country.iso2}
                    onClick={() => handleSelect(phoneCode)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors rounded-lg ${
                      selectedCode === phoneCode ? 'bg-brand-gold/10' : ''
                    }`}
                  >
                    <CountryFlag iso2={country.iso2} className="w-6" />
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] text-text-default">{country.name}</span>
                      </div>
                    </div>
                    <span className="text-brand-gold font-mono text-sm">{phoneCode}</span>
                    {selectedCode === phoneCode && (
                      <div className="w-2 h-2 rounded-full bg-brand-gold" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          {/* Bottom spacer for safe scrolling */}
          <div className="h-4" />
        </div>
      </DrawerContent>
    </Drawer>
  );
}
